// FIG. Recent runs across Submit's flat band (DESIGN_SPEC §11.2 below the fold): the newest runs in the Analyses row
// grammar (§11.3, §12.17; RESTYLE §5): verdict · state (banner with score, LIVE, QUEUED) | sample | environment (from
// 1200px) | ran | submitted.
//   <RecentRuns no />     no = the figure number on this page
// The rows come from the same effective-row machinery as Analyses (useEffectiveJobs: stream peeks for live runs, the
// behavior report or stream for verdict-less ones), so both lists say the same thing about a run: its state as soon as
// the guest starts, a partial verdict for a cancelled run, the run time from its stream.
import { useEffect, useMemo, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Alert, Arrow, Empty, FigHead, figTitleId, Loading, MicroTag, Time } from "@/components/ui";
import { useClock, useJobs } from "@/hooks";
import { isApiError } from "@/lib/api";
import { hash8, utc } from "@/lib/format";
import { signatureOf, tagNames } from "./verdictFields";
import { jobName } from "@/lib/records";
import { StateView } from "@/features/analyses/JobRow";
import { effectiveOf, queuePositions, rowView, sortNewest, type Eff } from "@/features/analyses/listState";
import { useEffectiveJobs } from "@/features/analyses/useEffectiveJobs";
import type { Job } from "@/types";
import { SEEN_LIMIT } from "./useSeenBefore";

const ROWS = 8;

function Row({ eff, queuePos, parent, observe }: { eff: Eff; queuePos?: number; parent?: Job; observe: (el: HTMLElement, id: string) => () => void }) {
  const navigate = useNavigate();
  const now = useClock(1000, eff.live);
  const job = eff.job;
  const to = `/jobs/${job.id}`;
  const open = () => void navigate(to, { viewTransition: true });
  const view = rowView(eff, { now, queuePos });
  const partial = eff.bucket === "cancelled" && eff.verdict ? `partial · ${eff.verdict.label} ${eff.verdict.score}` : null;
  const ref = useRef<HTMLTableRowElement>(null);
  useEffect(() => {
    const el = ref.current;
    return el ? observe(el, job.id) : undefined;
  }, [job.id, observe]);
  return (
    <tr
      ref={ref}
      tabIndex={0}
      className="cursor-pointer"
      onClick={(e) => {
        if ((e.target as HTMLElement).closest("a, button")) return;
        open();
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter" && e.target === e.currentTarget) open();
      }}
    >
      <td title={job.state === "failed" || job.state === "cancelled" ? job.reason : undefined}>
        <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-1">
          <StateView cell={view.state} />
          {partial && <span className="meta">{partial}</span>}
        </span>
      </td>
      <td>
        <span className="inline-flex flex-wrap items-baseline gap-x-2.5 max-[720px]:grid max-[720px]:justify-items-start max-[720px]:gap-0">
          {/* the row is the Tab stop (Enter opens it); the link stays for Cmd- and middle-click */}
          <Link className="rowlink name" to={to} viewTransition translate="no" tabIndex={-1}>
            {jobName(job)}
          </Link>
          {job.sample_sha && (
            <span className="hash8" translate="no" title={job.sample_sha}>
              {hash8(job.sample_sha)}
            </span>
          )}
          {job.kind === "url" && <MicroTag>url</MicroTag>}
          {job.allow_input && <MicroTag>input on</MicroTag>}
        </span>
        {job.parent_job_id && (
          <span className="block text-meta text-ink-faint" translate="no">
            ↳ child of {parent ? jobName(parent) : job.parent_job_id.slice(0, 8)}
          </span>
        )}
        {job.submitter?.name && (
          <span className="block text-meta text-ink-faint" translate="no">
            by {job.submitter.name}
          </span>
        )}
      </td>
      <td className="env max-[1199px]:hidden">
        {job.profile} · {job.network}
      </td>
      <td data-none={signatureOf(job) ? undefined : ""} title={signatureOf(job)}>
        {signatureOf(job) ?? "—"}
      </td>
      <td data-none={tagNames(job).length === 0 ? "" : undefined}>
        <TagsCell tags={tagNames(job)} />
      </td>
      <td className="r" data-none={view.ran ? undefined : ""} title={view.ran?.title}>
        {view.ran ? (
          <>
            {view.ran.text}
            {view.ran.of && <span className="text-meta text-ink-faint"> / {view.ran.of}</span>}
          </>
        ) : (
          "—"
        )}
      </td>
      <td className="r">
        <Time value={job.submitted_at}>{utc(job.submitted_at, "date-time", now)}</Time>
      </td>
    </tr>
  );
}

function TagsCell({ tags }: { tags: readonly string[] }) {
  if (!tags.length) return <>—</>;
  const shown = tags.slice(0, 3);
  const extra = tags.length - shown.length;
  return (
    <span className="inline-flex flex-wrap gap-1">
      {shown.map((t) => (
        <MicroTag key={t}>{t}</MicroTag>
      ))}
      {extra > 0 && <span className="text-meta text-ink-faint" title={tags.slice(3).join(", ")}>+{extra}</span>}
    </span>
  );
}

export function RecentRuns({ no }: { no: number }) {
  const q = useJobs(SEEN_LIMIT);
  const jobs = q.data;
  const newest = useMemo(() => (jobs ? sortNewest(jobs).slice(0, ROWS) : undefined), [jobs]);
  const { rows, observe } = useEffectiveJobs(newest);
  const byId = useMemo(() => new Map((jobs ?? []).map((j) => [j.id, j])), [jobs]);
  // places in each profile's queue, counted over every loaded run (a queued record has no stream to disagree with)
  const queue = useMemo(() => queuePositions((jobs ?? []).filter((j) => j.state === "queued").map((j) => effectiveOf(j))), [jobs]);

  const total = jobs?.length ?? 0;
  const totalText = total >= SEEN_LIMIT ? `${SEEN_LIMIT}+` : String(total);

  let body;
  if (!jobs) {
    body = q.isError ? (
      <Alert head="Couldn't load analyses">{isApiError(q.error) ? `${q.error.status} from ${q.error.path}` : "no answer from /v1/jobs"} · retrying every 5 s</Alert>
    ) : (
      <Loading>Loading analyses…</Loading>
    );
  } else if (!rows.length) {
    body = <Empty inline>No analyses yet.</Empty>;
  } else {
    body = (
      <div className={q.isError ? "tscroll is-stale" : "tscroll"}>
        <table className="t t--select">
          <thead>
            <tr>
              <th scope="col">verdict · state</th>
              <th scope="col">sample</th>
              <th scope="col" className="max-[1199px]:hidden">
                environment
              </th>
              <th scope="col">signature</th>
              <th scope="col">tags</th>
              <th scope="col" className="r">
                ran
              </th>
              <th scope="col" className="r" aria-sort="descending">
                submitted ↓
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((e) => (
              <Row key={e.id} eff={e} queuePos={queue.get(e.id)} parent={e.job.parent_job_id ? byId.get(e.job.parent_job_id) : undefined} observe={observe} />
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <section className="cell fig" aria-labelledby={figTitleId(no)}>
      <FigHead no={no} label="Recent runs" count={jobs ? `${rows.length} of ${totalText} · newest first` : undefined} />
      <div className="fig-body">{body}</div>
      {jobs && jobs.length > 0 && (
        <p className="mt-4">
          <Link className="action" to="/jobs" viewTransition>
            All {totalText} analyses
            <Arrow />
          </Link>
        </p>
      )}
    </section>
  );
}
